import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import RecaptchaBox, { isRecaptchaEnabled } from "@/components/RecaptchaBox";
import { toast } from "sonner";
import { Mail, Lock } from "lucide-react";
import { checkLoginRateLimit, recordLoginAttempt, clearLoginRateLimit, formatRemainingTime } from "@/lib/rateLimit";
import { createHoneypotInitialState, isHoneypotTriggered, logSecurityEvent } from "@/lib/security";

export default function Login() {
  const { login, loginWithGoogle, resetPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [honeypot, setHoneypot] = useState(createHoneypotInitialState());

  const submit = async (e) => {
    e.preventDefault();

    // Honeypot check — if filled, silently reject (bot detected)
    if (isHoneypotTriggered(honeypot)) {
      logSecurityEvent("login_honeypot", { email });
      // Pretend success to not alert bots
      toast.success("ပြန်လည်ကြိုဆိုပါတယ်!");
      navigate(from, { replace: true });
      return;
    }

    // Rate limit check
    const rateLimit = checkLoginRateLimit();
    if (!rateLimit.allowed) {
      toast.error(`ဝင်ရောက်ရန် ခဏစောင့်ပါ (${formatRemainingTime(rateLimit.remainingMs)})`);
      logSecurityEvent("login_rate_limited", { email });
      return;
    }

    if (isRecaptchaEnabled() && !captchaToken) {
      toast.error("Please complete the CAPTCHA first.");
      return;
    }

    setLoading(true);
    recordLoginAttempt();
    try {
      await login(email, password, captchaToken);
      clearLoginRateLimit();
      toast.success("ပြန်လည်ကြိုဆိုပါတယ်!");
      navigate(from, { replace: true });
    } catch (err) {
      logSecurityEvent("login_failed", { email, error: err?.message });
      toast.error(err?.response?.data?.detail || err?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = async () => {
    setGoogleLoading(true);
    try {
      await loginWithGoogle();
      toast.success("ပြန်လည်ကြိုဆိုပါတယ်!");
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err?.message || "Google login failed");
    } finally {
      setGoogleLoading(false);
    }
  };

  const forgotPassword = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      toast.error("Please enter your email first.");
      return;
    }
    setResetLoading(true);
    try {
      await resetPassword(trimmedEmail);
      toast.success("Password reset email sent. Please check your inbox.");
    } catch (err) {
      toast.error(err?.message || "Could not send password reset email");
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid place-items-center px-6 py-12">
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-3xl p-8">
        <h1 className="font-bold text-3xl">ဝင်ရောက်ရန်</h1>
        <p className="text-zinc-500 mt-1 text-sm">MyanTax Box သို့ ပြန်လည်ကြိုဆိုပါတယ်</p>

        <form onSubmit={submit} className="space-y-4 mt-6">
          {/* Honeypot fields — hidden from real users, bots will fill these */}
          <div className="absolute opacity-0 h-0 w-0 overflow-hidden pointer-events-none" aria-hidden="true" tabIndex={-1}>
            <input type="text" name="website" value={honeypot.website} onChange={(e) => setHoneypot(h => ({ ...h, website: e.target.value }))} tabIndex={-1} autoComplete="off" />
            <input type="text" name="name_hp" value={honeypot.name_hp} onChange={(e) => setHoneypot(h => ({ ...h, name_hp: e.target.value }))} tabIndex={-1} autoComplete="off" />
          </div>
          <div>
            <Label>အီးမေးလ်</Label>
            <div className="relative mt-1.5">
              <Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 pl-10" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between gap-3">
              <Label>စကားဝှက်</Label>
              <button
                type="button"
                onClick={forgotPassword}
                disabled={resetLoading}
                className="text-xs font-medium text-[#5FB259] hover:underline disabled:cursor-not-allowed disabled:opacity-60"
              >
                {resetLoading ? "ပို့နေသည်…" : "စကားဝှက်မေ့နေပါသလား?"}
              </button>
            </div>
            <div className="relative mt-1.5">
              <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 pl-10" />
            </div>
          </div>
          <RecaptchaBox onTokenChange={setCaptchaToken} />
          <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full h-11 bg-[#7CC576] hover:bg-[#5FB259] rounded-lg">
            {loading ? "ဝင်ရောက်နေသည်…" : "ဝင်ရောက်မည်"}
          </Button>
        </form>

        <div className="my-6 flex items-center gap-3 text-xs text-zinc-400">
          <div className="flex-1 h-px bg-zinc-200" />
          <span className="tracking-wide">သို့မဟုတ်</span>
          <div className="flex-1 h-px bg-zinc-200" />
        </div>

        <Button data-testid="google-login-btn" type="button" variant="outline" onClick={googleLogin} disabled={googleLoading} className="w-full h-11 rounded-lg border-zinc-300">
          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          {googleLoading ? "ဝင်ရောက်နေသည်…" : "Google ဖြင့် ဆက်လုပ်ရန်"}
        </Button>

        <p className="text-center text-sm text-zinc-500 mt-6">
          အကောင့်မရှိသေးဘူးလား?{" "}
          <Link to="/register" className="text-[#7CC576] font-medium hover:underline">အကောင့်ဖွင့်ရန်</Link>
        </p>
      </div>
    </div>
  );
}
